import { FC, MouseEventHandler } from 'react';

import { Badge, Paragraph } from '@purpur/library';

import { useTranslations } from '@telialt/common-cms/src/useTranslations';
import { withParams } from '@telialt/utils/src/helpers';

import { ScreenReaderOnly } from '../ScreenReaderOnly/ScreenReaderOnly.styles';
import {
    ProductCardSmallBadges,
    ProductCardSmallCommitment,
    ProductCardSmallContent,
    ProductCardSmallFullPrice,
    ProductCardSmallFullPriceOld,
    ProductCardSmallFullPrices,
    ProductCardSmallImage,
    ProductCardSmallImageContainer,
    ProductCardSmallMonthlyPrice,
    ProductCardSmallMonthlyPriceLabel,
    ProductCardSmallMonthlyPriceUnit,
    ProductCardSmallProductName,
    ProductCardSmallWrapper,
    ResidualValue,
} from './ProductCardSmall.styles';

type Props = {
    name: string;
    image: string;
    imageAlt: string;
    monthlyPrice?: string;
    commitment?: string;
    fullPrice?: string;
    oldFullPrice?: string;
    residualValue?: string;
    badges?: string[];
    url: string;
    dataTest?: string;
    onClick?: MouseEventHandler<HTMLAnchorElement>;
};

export const ProductCardSmall: FC<Props> = ({
    name,
    image,
    imageAlt,
    badges,
    monthlyPrice,
    commitment,
    fullPrice,
    oldFullPrice,
    residualValue,
    url,
    dataTest,
    onClick,
}) => {
    const { t } = useTranslations();

    const hasBadges = badges?.length > 0;

    const labelMonthlyPrice =
        !!monthlyPrice &&
        [
            t['search.component.product_card_small.monthly_price_label'],
            monthlyPrice,
            t['search.component.product_card_small.monthly_price_unit_accessible'],
        ].join(' ');
    const labelFullPrice =
        !!fullPrice && withParams(t['search.component.product_card_small.full_price'], { price: fullPrice });

    const labelPrice = labelMonthlyPrice || labelFullPrice || '';

    return (
        <ProductCardSmallWrapper
            href={url}
            onClick={onClick}
            $hasBadges={hasBadges}
            data-test={dataTest}
            aria-label={`${name}, ${labelPrice}`}
        >
            {hasBadges && (
                <ProductCardSmallBadges>
                    {badges.map((badge, index) => (
                        <Badge key={`badge-${index}`} showIcon={false} variant="information">
                            {badge}
                        </Badge>
                    ))}
                </ProductCardSmallBadges>
            )}

            <ProductCardSmallImageContainer>
                <ProductCardSmallImage src={image} alt={imageAlt || name} />
            </ProductCardSmallImageContainer>

            <ProductCardSmallContent>
                <ProductCardSmallProductName tag="h4">{name}</ProductCardSmallProductName>

                {!!monthlyPrice && (
                    <Paragraph variant="paragraph-100-bold">
                        <ProductCardSmallMonthlyPriceLabel>
                            {t['search.component.product_card_small.monthly_price_label']}
                        </ProductCardSmallMonthlyPriceLabel>
                        &nbsp;
                        <ProductCardSmallMonthlyPrice>{monthlyPrice}</ProductCardSmallMonthlyPrice>
                        &nbsp;
                        <ProductCardSmallMonthlyPriceUnit
                            aria-label={t['search.component.product_card.monthly_price_unit_accessible']}
                        >
                            <span aria-hidden="true">
                                {t['search.component.product_card_small.monthly_price_unit']}
                            </span>
                            <ScreenReaderOnly>
                                {t['search.component.product_card.monthly_price_unit_accessible']}
                            </ScreenReaderOnly>
                        </ProductCardSmallMonthlyPriceUnit>
                    </Paragraph>
                )}

                {commitment && <ProductCardSmallCommitment>{commitment}</ProductCardSmallCommitment>}

                {!!residualValue && (
                    <ResidualValue>
                        {withParams(t['search.component.product_card_small.residual_value'], {
                            price: residualValue,
                        })}
                    </ResidualValue>
                )}

                <ProductCardSmallFullPrices>
                    <ProductCardSmallFullPrice>
                        {withParams(t['search.component.product_card_small.full_price'], { price: fullPrice })}
                    </ProductCardSmallFullPrice>
                    &nbsp;
                    {!!oldFullPrice && (
                        <ProductCardSmallFullPriceOld aria-hidden="true">
                            {withParams(t['search.component.product_card_small.old_full_price'], {
                                price: oldFullPrice,
                            })}
                        </ProductCardSmallFullPriceOld>
                    )}
                </ProductCardSmallFullPrices>
            </ProductCardSmallContent>
        </ProductCardSmallWrapper>
    );
};
